import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CubeIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { purchaseService } from '../../services/purchaseService'
import toast from 'react-hot-toast'

interface PurchaseItem {
  item_id: number
  product_id: number
  quantity: number
  received_quantity: number
  unit_price: number
  unit_price_base: number
  products: {
    product_id: number
    product_name: string
    sku: string
    track_serial_numbers?: boolean
    product_images?: { image_url: string }[]
  }
}

interface Purchase {
  purchase_id: number
  purchase_number: string
  purchase_date: string
  status: string
  currency_code: string
  suppliers: {
    company_name: string
  } | null
  purchase_items: PurchaseItem[]
}

interface ReceiveItem {
  item_id: number
  serials: string[]
}

export const ReceivePurchasePage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => purchaseService.getById(id!),
  })

  const purchase: Purchase = data?.data?.data

  const pendingItems = purchase?.purchase_items
    ?.filter((item) => item.received_quantity < item.quantity)
    ?.map((item) => ({
      ...item,
      pending: item.quantity - item.received_quantity,
    })) || []

  const [receiveItems, setReceiveItems] = useState<Record<number, string[]>>({})

  const receiveMutation = useMutation({
    mutationFn: (payload: { received_items: ReceiveItem[] }) =>
      purchaseService.receive(id!, payload),
    onSuccess: () => {
      toast.success('Items received successfully')
      navigate(`/purchases/${id}`)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to receive items')
    },
  })

  const getSerialsForItem = (itemId: number) => receiveItems[itemId] || []

  const addSerialField = (itemId: number) => {
    setReceiveItems((prev) => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ''],
    }))
  }

  const removeSerialField = (itemId: number, index: number) => {
    setReceiveItems((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter((_, i) => i !== index),
    }))
  }

  const updateSerial = (itemId: number, index: number, value: string) => {
    setReceiveItems((prev) => {
      const serials = [...(prev[itemId] || [])]
      serials[index] = value
      return { ...prev, [itemId]: serials }
    })
  }

  const handleReceiveAll = () => {
    const received_items: ReceiveItem[] = []
    for (const item of pendingItems) {
      const serials = getSerialsForItem(item.item_id).filter((s) => s.trim() !== '')
      if (serials.length === 0) continue
      if (serials.length > item.pending) {
        toast.error(`Too many serials for ${item.products.product_name}. Max: ${item.pending}`)
        return
      }
      received_items.push({ item_id: item.item_id, serials })
    }

    if (received_items.length === 0) {
      toast.error('Please enter at least one serial number')
      return
    }

    receiveMutation.mutate({ received_items })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!purchase) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Purchase not found</p>
        <Link to="/purchases" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to purchases
        </Link>
      </div>
    )
  }

  const totalPending = pendingItems.reduce((sum, item) => sum + item.pending, 0)

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to={`/purchases/${id}`} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Receive Items</h1>
            <p className="text-sm text-gray-500">
              {purchase.purchase_number} — {purchase.suppliers?.company_name || 'Unknown Supplier'}
            </p>
          </div>
        </div>
      </div>

      {totalPending === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">All items received</h3>
          <p className="text-gray-500 mb-4">This purchase order is fully received.</p>
          <Link
            to={`/purchases/${id}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Purchase
          </Link>
        </div>
      ) : (
        <>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
            <CubeIcon className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Enter serial numbers for the items you are receiving
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {totalPending} item{totalPending > 1 ? 's' : ''} pending receipt across {pendingItems.length} product{pendingItems.length > 1 ? 's' : ''}.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {pendingItems.map((item) => {
              const serials = getSerialsForItem(item.item_id)
              return (
                <div
                  key={item.item_id}
                  className="bg-white rounded-xl shadow border border-gray-100 p-6"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.products.product_images?.[0] ? (
                        <img
                          src={item.products.product_images[0].image_url}
                          alt={item.products.product_name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <CubeIcon className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{item.products.product_name}</h3>
                      <p className="text-sm text-gray-500">{item.products.sku}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-gray-600">
                          Ordered: <strong>{item.quantity}</strong>
                        </span>
                        <span className="text-sm text-gray-600">
                          Received: <strong>{item.received_quantity}</strong>
                        </span>
                        <span className="text-sm text-yellow-700 font-medium">
                          Pending: {item.pending}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Serial Numbers</label>
                      <span className="text-xs text-gray-500">
                        {serials.filter((s) => s.trim() !== '').length} of {item.pending} entered
                      </span>
                    </div>

                    {serials.length === 0 && (
                      <button
                        onClick={() => addSerialField(item.item_id)}
                        className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Serial Number
                      </button>
                    )}

                    {serials.map((serial, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                        <input
                          type="text"
                          value={serial}
                          onChange={(e) => updateSerial(item.item_id, idx, e.target.value)}
                          placeholder={`Serial #${idx + 1}`}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <button
                          onClick={() => removeSerialField(item.item_id, idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Remove"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    {serials.length > 0 && serials.length < item.pending && (
                      <button
                        onClick={() => addSerialField(item.item_id)}
                        className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium mt-1"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Another Serial
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Link
              to={`/purchases/${id}`}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </Link>
            <button
              onClick={handleReceiveAll}
              disabled={receiveMutation.isPending}
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
            >
              <CheckCircleIcon className="w-4 h-4" />
              {receiveMutation.isPending ? 'Receiving...' : 'Confirm Receipt'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
